/**
 * Enter a code from an authenticator app — or, when the person has none set up
 * yet, set one up (QR code and key) and enter its first code. On success the
 * session is signed in with a second factor (aal2).
 *
 * Used by the sign-in gate (SecondFactorGate) and by an administrator setting
 * up their own before requiring one for a tenant.
 */

import { useEffect, useState } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import {
  startAuthenticatorEnrollment,
  verifiedAuthenticator,
  verifyAuthenticatorCode,
  type AuthenticatorEnrollment,
} from '../../lib/identityService';

type Mode =
  | { kind: 'loading' }
  | { kind: 'enter'; factorId: string }
  | { kind: 'set-up'; enrollment: AuthenticatorEnrollment };

export function AuthenticatorCodeForm({
  onVerified,
  onMode,
}: {
  onVerified: () => void;
  /** Told whether the person is entering a code or setting the app up, for the caller's heading. */
  onMode?: (mode: 'enter' | 'set-up') => void;
}) {
  const [mode, setMode] = useState<Mode>({ kind: 'loading' });
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const factor = await verifiedAuthenticator();
        if (cancelled) return;
        if (factor) {
          setMode({ kind: 'enter', factorId: factor.id });
          onMode?.('enter');
          return;
        }
        onMode?.('set-up');
        const enrollment = await startAuthenticatorEnrollment();
        if (!cancelled) setMode({ kind: 'set-up', enrollment });
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not start the authenticator setup.');
      }
    })();
    return () => {
      cancelled = true;
    };
    // onMode is a notification, not an input
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const factorId = mode.kind === 'enter' ? mode.factorId : mode.kind === 'set-up' ? mode.enrollment.factorId : null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!factorId) return;
    setBusy(true);
    setError(null);
    try {
      await verifyAuthenticatorCode(factorId, code);
      onVerified();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That code did not work.');
      setBusy(false);
    }
  };

  return (
    <div>
      {mode.kind === 'set-up' && (
        <div className="mb-6">
          <p className="text-sm text-[var(--text-sec)]">
            Scan this with an authenticator app (1Password, Google Authenticator, Microsoft Authenticator), then enter the six-digit code it shows.
          </p>
          <img src={mode.enrollment.qrCode} alt="QR code for your authenticator app" className="mx-auto mt-4 h-48 w-48 bg-white p-2" />
          <p className="mt-3 text-xs text-[var(--text-ter)]">
            Can’t scan it? Enter this key in the app: <span className="font-mono break-all text-[var(--text-sec)]">{mode.enrollment.secret}</span>
          </p>
        </div>
      )}
      {mode.kind === 'enter' && (
        <p className="mb-6 text-sm text-[var(--text-sec)]">Open your authenticator app and enter the six-digit code it shows for Priorities.ai.</p>
      )}
      {mode.kind === 'loading' && !error && (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-[var(--text-ter)]" />
        </div>
      )}

      <form onSubmit={submit} className="space-y-4">
        <div>
          <label htmlFor="second-factor-code" className="block text-sm font-medium text-[var(--text-sec)] mb-1.5">
            Six-digit code
          </label>
          <input
            id="second-factor-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9 ]*"
            maxLength={7}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="no-dark w-full px-4 py-3 bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule-bright)] font-mono text-lg tracking-[0.3em] text-[var(--text-pri)] outline-none focus:border-[var(--text-pri)]"
            disabled={busy || !factorId}
            autoFocus
          />
        </div>
        {error && (
          <div className="flex items-start gap-2 text-sm text-[var(--coral)] bg-[var(--coral-dim)] p-3" style={{ borderLeft: '3px solid var(--coral)' }}>
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={busy || !factorId || code.replace(/\s+/g, '').length !== 6}
          className="w-full flex items-center justify-center gap-2 py-3.5 bg-[var(--cta)] text-[var(--cta-ink)] font-heading font-semibold hover:bg-[var(--cta-hover)] disabled:opacity-50 transition-colors"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {mode.kind === 'set-up' ? 'Turn it on and continue' : 'Continue'}
        </button>
      </form>
    </div>
  );
}
